import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from 'tss-react/mui';
import { SavingPanelContent } from './SavingPanelContent';
import { useNavigate } from 'react-router-dom';
import { VIDEO_SAVING_STEP_STATUS } from "../../../CreatePageConstants";
import { createActions } from "../../../store";
import { DEFAULT_VIDEO_SAVING_STATE } from '../../../store/reducer';

export const styles = (theme) => ({
    '@keyframes spin': {
        'from': {
            transform: 'rotate(0deg)',
        },
        'to': {
            transform: 'rotate(360deg)',
        },
    },
    saveWidget: {
        position: 'relative',
        zIndex: "100",
        fontFamily: 'Inter, sans-serif',
        backgroundColor: theme.palette.background.paper,
        color: '#e6edf3',
        padding: theme.spacing(3),
        width: '100%',
        maxWidth: '370px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
    },
    title: {
        color: "white",
        fontWeight: 'bold',
        fontFamily: 'Inter, sans-serif',
        transition: 'color 0.3s ease',
    },
    titleFailed: {
        color: '#f87171'
    },
    progressContainer: {
        marginBottom: theme.spacing(3),
    },
    progressBar: {
        height: '8px',
        borderRadius: '4px',
        backgroundColor: 'rgba(139, 148, 158, 0.3)',
        '& .MuiLinearProgress-bar': {
            transition: 'transform .4s linear, background-color .3s ease',
            borderRadius: '4px',
        },
    },
    progressBarBlue: { '& .MuiLinearProgress-bar': { backgroundColor: '#3b82f6' } },
    progressBarRed: { '& .MuiLinearProgress-bar': { backgroundColor: '#ef4444' } },

    stepsList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    stepItem: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: '7px 0',
        gap: '12px',
    },
    stepIcon: {
        minWidth: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    stepContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    stepName: {
        color: theme.palette.grey[400],
        fontSize: '0.875rem',
        lineHeight: 1.4,
        fontWeight: 500,
    },
    stepMessage: {
        color: theme.palette.grey[500],
        fontSize: '0.75rem',
        lineHeight: 1.3,
    },
    stepText: {
        color: theme.palette.grey[400],
    },
    stepTextActive: {
        color: '#e6edf3',
    },
    statusText: {
        fontSize: '0.875rem',
        fontWeight: 400,
    },
    iconQueued: { color: theme.palette.grey[600] },
    iconInProgress: {
        color: '#60a5fa',
        animation: '$spin 1s linear infinite',
    },
    iconCompleted: { color: '#4ade80' },
    iconFailed: { color: '#f87171' },
    iconCancelled: { color: theme.palette.grey[600] },

    textQueued: { color: theme.palette.grey[500] },
    textInProgress: { color: '#60a5fa' },
    textCompleted: { color: '#4ade80' },
    textFailed: { color: '#f87171' },
    textCancelled: { color: theme.palette.grey[500] },
    buttonContainer: {
        marginTop: theme.spacing(3),
        textAlign: 'center',
    },
    button: {
        textTransform: 'none',
        fontWeight: 'bold',
    },
    retryButton: {
        backgroundColor: '#dc2626',
        color: 'white',
        '&:hover': {
            backgroundColor: '#b91c1c',
        }
    },
});

// Mapping between store keys and display information for video saving steps.
// The order here is the order saveVideoProject runs them in, so a step never starts before the one
// above it has finished. Uploading the pictures of the image elements to storage and linking them to
// the video are handled sequentially in a single 'Saving images' step.
export const STEP_MAPPING = [
    { storeKey: 'emoji', id: 'emoji', name: 'Saving emoji', status: "Unchanged" },
    { storeKey: 'settings', id: 'settings', name: 'Saving configuration', status: "Unchanged" },
    { storeKey: 'audio', id: 'audio', name: 'Saving audio', status: "Unchanged" },
    { storeKey: 'background', id: 'background', name: 'Saving background', status: "Unchanged" },
    { storeKey: 'logo', id: 'logo', name: 'Saving logo', status: "Unchanged" },
    { storeKey: 'imageElements', id: 'imageElements', name: 'Saving images', status: "Unchanged" },
    { storeKey: 'thumbnail', id: 'thumbnail', name: 'Creating thumbnail', status: "Unchanged" },
];

// Convert video saving step status constants to display strings
export const getVideoStepStatus = (status) => {
    switch (status) {
         case VIDEO_SAVING_STEP_STATUS.UNCHANGED:
            return 'Unchanged';
        case VIDEO_SAVING_STEP_STATUS.PENDING:
            return 'Pending';
        case VIDEO_SAVING_STEP_STATUS.COMPLETED:
            return 'Completed';
        case VIDEO_SAVING_STEP_STATUS.IN_PROGRESS:
            return 'Progress';
        case VIDEO_SAVING_STEP_STATUS.FAILED:
            return 'Failed';
        case VIDEO_SAVING_STEP_STATUS.CANCELLED:
            return 'Cancelled';
        default:
            return 'Pending';
    }
};

// Filters the saving steps to determine which ones should be displayed.
// The 'emoji' step is only shown if it has failed, allowing users to see the error.
// All other steps are hidden if their status is 'Unchanged' to reduce clutter.
const filterSavingSteps = (step) => {
  // Special rule for the 'emoji' step: only show it if it has failed.
  if (step.id === 'emoji') {
    return step.status === 'Failed';
  }
  // For all other steps, hide them if they are unchanged.
  return step.status !== 'Unchanged';
};

const SavingPanel = ({
    classes, videoSavingState,
    setProperty, saveVideoProject
}) => {
    const navigate = useNavigate();
    
    // Calculate steps data, overall status, and progress from Redux state
    const { steps, overallStatus, progress } = useMemo(() => {
        // Transform store data into step objects for display
        const processedSteps = STEP_MAPPING.map(({ storeKey, id, name }) => {
            const stepData = videoSavingState[storeKey] || (storeKey === 'imageElements' ? videoSavingState.images : undefined) || {};
            return {
                id,
                name,
                status: getVideoStepStatus(stepData.status),
                percentage: stepData?.percentage ?? 0,
                message: stepData.message,
            };
        }).filter(filterSavingSteps);

        // Calculate overall progress percentage
        const totalPercentage = processedSteps.length > 0 
            ? processedSteps.reduce((sum, step) => sum + step.percentage, 0) / processedSteps.length 
            : 0;

        // Determine overall status based on step states
        const determineOverallStatus = () => {
            if (videoSavingState.savingInProgress) {
                return 'saving';
            }
            
            const hasFailed = processedSteps.some(step => step.status === 'Failed');
            const allCompleted = processedSteps.length > 0 && 
                                processedSteps.every(step => step.status === 'Completed');

            if (hasFailed) {
                return 'failed';
            } else if (allCompleted && totalPercentage >= 100) {
                return 'completed';
            }
            
            return 'saving';
        };

        return {
            steps: processedSteps,
            overallStatus: determineOverallStatus(),
            progress: totalPercentage,
        };
    }, [videoSavingState]);

    const retry = () => {
        setProperty("videoSavingState", { ...DEFAULT_VIDEO_SAVING_STATE });
        saveVideoProject(null, true, navigate)
    };

    return (
        <SavingPanelContent
            classes={classes}
            steps={steps}
            overallStatus={overallStatus}
            progress={progress}
            retry={retry}
        />
    );
};

const mapStateToProps = (state) => ({
    videoSavingState: state.create.videoSavingState,
});

const mapDispatchToProps = (dispatch) => ({
    saveVideoProject: (onComplete, showSavingPanel, navigate) => dispatch(createActions.saveVideoProject(onComplete, showSavingPanel, navigate)),
    setProperty: (key, value) => dispatch(createActions.setProperty(key, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(SavingPanel, styles));